<template>
  <div class="tin-root">
    <div class="ambient"></div>
    <div class="ambient-floor"></div>
    <div class="grain"></div>
    <div class="frame"></div>
    <div class="frame-inner"></div>

    <div class="page">

      <div class="wm-row">
        <div class="wm-line"></div>
        <span class="wm-text">Club Trailz</span>
        <div class="wm-line r"></div>
      </div>

      <button class="back-link" @click="navigateBack">
        <span class="back-arrow">←</span> Back
      </button>

      <div class="section-hd">
        <div class="section-rule l"></div>
        <span class="section-eyebrow">Legal</span>
        <div class="section-rule"></div>
      </div>
      <h1 class="page-title">Terms <em>of Service</em></h1>
      <p class="page-sub">Effective {{ effectiveDate }} · Last updated {{ lastUpdated }}</p>

      <!-- ── TABLE OF CONTENTS ── -->
      <div class="toc">
        <button
          v-for="section in termsSections"
          :key="'toc-' + section.number"
          class="toc-item"
          @click="scrollToSection(section.number)"
        >
          <span class="toc-num">{{ section.number }}</span>
          <span class="toc-title">{{ section.title }}</span>
        </button>
      </div>

      <!-- ── DOCUMENT ── -->
      <div class="doc">
        <section
          v-for="section in termsSections"
          :key="section.number"
          :id="'section-' + section.number"
          class="doc-section"
        >
          <h2 class="doc-h2">{{ section.number }}. {{ section.title }}</h2>
          <p class="doc-sub">{{ section.subtitle }}</p>

          <p v-for="(p, i) in section.content" :key="'c-' + i" class="doc-p" v-html="p"></p>

          <ul v-if="section.bullets" class="doc-list">
            <li v-for="(b, i) in section.bullets" :key="'b-' + i" v-html="b"></li>
          </ul>

          <div v-if="section.alert" class="doc-alert" v-html="section.alert.text"></div>

          <p v-for="(p, i) in section.content2" :key="'c2-' + i" class="doc-p" v-html="p"></p>
        </section>
      </div>

      <!-- ── ACTIONS ── -->
      <div class="actions-row">
        <a class="btn-close" href="mailto:ricosevere@codeforgedev.com">
          <div class="btn-tick"></div>
          <span>Questions? Contact Us</span>
          <div class="btn-tick"></div>
        </a>
      </div>

      <div class="footer-links">
        <router-link :to="{ name: 'privacy' }" class="footer-link">Privacy &amp; Data Use</router-link>
        <span class="footer-sep">·</span>
        <router-link to="/" class="footer-link">Back to Home</router-link>
      </div>
      <div class="footer-copyright">© {{ currentYear }} Club Trailz. All rights reserved.</div>

    </div>
  </div>
</template>

<script>
export default {
  name: "TermsView",

  data() {
    return {
      termsSections: [
        {
          number: 1,
          title: "Acceptance",
          subtitle: "Agreement to terms",
          content: [
            "By accessing or using Club Trailz (the \"Service\"), you agree to be bound by these Terms of Service (\"Terms\"). If you do not agree to these Terms, you may not access or use the Service.",
            "Your continued use of the Service constitutes acceptance of any updates or modifications to these Terms.",
          ],
        },
        {
          number: 2,
          title: "Membership & Eligibility",
          subtitle: "Access requirements and responsibilities",
          content: [
            "Club Trailz operates on an invite-only basis. We maintain the right to approve, defer, or revoke membership at our sole discretion to preserve community quality and standards.",
          ],
          bullets: [
            "You must provide accurate, complete, and current information during registration and keep your account credentials secure.",
            "You are solely responsible for all activity that occurs under your account.",
            "You must be at least 18 years of age to use the Service.",
            "You may not transfer or share your account with any third party.",
          ],
        },
        {
          number: 3,
          title: "Community Standards & Confidentiality",
          subtitle: "Maintaining trust and professionalism",
          content: [
            "Our community thrives on mutual respect and confidentiality. All members are expected to maintain the highest standards of professional conduct.",
          ],
          bullets: [
            "<strong>Respect privacy:</strong> Do not copy, record, publish, or redistribute member content, conversations, or information without explicit written permission.",
            "<strong>No harassment or discrimination:</strong> Treat all members with respect. Harassment, hate speech, or discriminatory behavior of any kind is strictly prohibited.",
            "<strong>No spam or unauthorized solicitation:</strong> Do not send unsolicited commercial messages or engage in mass outreach without consent.",
            "<strong>No scraping or automated access:</strong> Do not use bots, scrapers, or automated tools to access the Service.",
            "<strong>Legitimate use only:</strong> Use the Service exclusively for professional networking and relationship building.",
          ],
          alert: {
            text: "<strong>Violations may result in immediate account suspension or termination.</strong>",
          },
        },
        {
          number: 4,
          title: "Content & Intellectual Property",
          subtitle: "Rights and ownership",
          content: [
            "You retain full ownership of all content you post, share, or submit through the Service. By posting content, you grant Club Trailz a limited, non-exclusive, revocable, worldwide license to host, display, and distribute your content solely for the purpose of operating and improving the Service.",
            "This license terminates when you delete your content or close your account, except where content has been shared with other members or where retention is required by law.",
            "The Club Trailz name, logo, branding, and platform software are the exclusive intellectual property of Club Trailz and are protected by copyright, trademark, and other laws.",
          ],
        },
        {
          number: 5,
          title: "Matches & Introductions",
          subtitle: "How we connect members",
          content: [
            "We use your provided preferences, professional information, and activity patterns to suggest relevant member connections and chapters. Our matching is designed to facilitate high-quality, meaningful connections.",
            "All matches and introductions are best-effort suggestions based on available data. They do not constitute endorsements, guarantees, recommendations, or professional advice of any kind.",
            "Club Trailz is not responsible for the outcome of any connection, relationship, or arrangement that results from our introductions.",
          ],
        },
        {
          number: 6,
          title: "Fees & Billing",
          subtitle: "Payment terms and refunds",
          content: [
            "If you purchase a paid membership plan, you authorize Club Trailz and our payment processor to charge your payment method for all applicable fees, including subscription charges and applicable taxes.",
            "Subscription fees are billed in advance on a recurring basis according to your selected billing cycle (monthly or annually).",
            "<strong>Refund Policy:</strong> Unless explicitly stated otherwise or required by applicable law, all fees are non-refundable. We do not provide refunds or credits for partial periods of service, unused time, or unused features.",
            "We reserve the right to modify pricing with advance notice. Price changes will not affect your current billing cycle and will take effect upon renewal.",
          ],
        },
        {
          number: 7,
          title: "Privacy & Data Protection",
          subtitle: "How we handle your information",
          content: [
            "We take your privacy seriously and are committed to protecting your personal information. <strong>We do not sell, rent, or trade your personal data to third parties.</strong>",
            "Our comprehensive <a href='/privacy'>Privacy Policy</a> explains in detail what information we collect, how we use it, how we protect it, and your rights regarding your data.",
            "By using the Service, you consent to the collection and use of your information as described in our Privacy Policy.",
          ],
        },
        {
          number: 8,
          title: "Third-Party Services",
          subtitle: "External integrations and links",
          content: [
            "The Service may contain links to third-party websites, services, or integrations that are not owned or controlled by Club Trailz. We are not responsible for the content, privacy policies, or practices of any third-party services.",
            "Your use of third-party services is governed by their respective terms and privacy policies. We recommend reviewing these documents before using any third-party service.",
            "Any integration with third-party services requires your explicit consent and can be revoked at any time from your account settings.",
          ],
        },
        {
          number: 9,
          title: "Disclaimers & Warranties",
          subtitle: "Service limitations",
          content: [
            "The Service is provided on an \"as is\" and \"as available\" basis without warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.",
            "We do not guarantee that the Service will be uninterrupted, secure, or error-free. We do not warrant the accuracy, completeness, or reliability of any content or information obtained through the Service.",
            "We do not guarantee specific outcomes, connections, opportunities, or results from your use of the Service, including matches, introductions, or arrangements.",
          ],
        },
        {
          number: 10,
          title: "Limitation of Liability",
          subtitle: "Legal protections and limitations",
          content: [
            "To the fullest extent permitted by applicable law, Club Trailz, its officers, directors, employees, and agents will not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to:",
          ],
          bullets: [
            "Loss of profits, revenue, data, or business opportunities",
            "Loss of goodwill or reputation",
            "Personal injury or emotional distress",
            "Any damages resulting from your use or inability to use the Service",
            "Any damages resulting from interactions with other members",
          ],
          content2: [
            "In jurisdictions that do not allow the exclusion or limitation of liability for consequential or incidental damages, our liability is limited to the maximum extent permitted by law.",
            "Our total liability to you for all claims arising from your use of the Service will not exceed the amount you paid us in the twelve (12) months preceding the claim, or $100 USD, whichever is greater.",
          ],
        },
        {
          number: 11,
          title: "Suspension & Termination",
          subtitle: "Account closure and access restriction",
          content: [
            "We reserve the right to suspend or terminate your access to the Service immediately, with or without notice, for any reason, including but not limited to:",
          ],
          bullets: [
            "Violation of these Terms or our Community Standards",
            "Fraudulent, abusive, or illegal activity",
            "Actions that harm or could harm other members or the platform",
            "Extended periods of inactivity",
            "Non-payment of fees",
          ],
          content2: [
            "You may delete your account at any time through your account settings. Upon termination, your right to access and use the Service will immediately cease.",
            "Following termination, we may retain certain information as required by law or for legitimate business purposes, as outlined in our Privacy Policy.",
          ],
        },
        {
          number: 12,
          title: "Changes to Terms",
          subtitle: "Updates and modifications",
          content: [
            "We reserve the right to modify or update these Terms at any time at our sole discretion. Material changes will be communicated to you through the Service, via email, or by other reasonable means.",
            "Your continued use of the Service after any changes to these Terms constitutes acceptance of those changes. If you do not agree to the modified Terms, you must stop using the Service and may close your account.",
            "We encourage you to review these Terms periodically to stay informed of any updates.",
          ],
        },
        {
          number: 13,
          title: "Governing Law & Jurisdiction",
          subtitle: "Legal framework and dispute resolution",
          content: [
            "These Terms and any dispute arising out of or relating to them or the Service are governed by and construed in accordance with the laws of the Commonwealth of Massachusetts, United States of America, without regard to its conflict of laws principles and except to the extent preempted by U.S. federal law.",
            "You and Club Trailz agree to submit to the exclusive jurisdiction and venue of the state courts located in Suffolk County, Massachusetts, and the United States District Court for the District of Massachusetts, Boston Division, for any action or proceeding arising from or related to these Terms or your use of the Service that is not subject to arbitration.",
            "You hereby consent to personal jurisdiction in these courts and waive any objection to jurisdiction or venue in these courts.",
            "<strong>Important:</strong> By using the Service, you waive any right to a jury trial for disputes related to these Terms or the Service.",
          ],
        },
      ],
    };
  },

  computed: {
    lastUpdated() {
      return new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    },
    effectiveDate() {
      return this.lastUpdated;
    },
    currentYear() {
      return new Date().getFullYear();
    },
  },

  methods: {
    scrollToSection(number) {
      const el = document.getElementById("section-" + number);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    navigateBack() {
      if (window.history.length > 2) {
        this.$router.go(-1);
      } else {
        this.$router.push("/");
      }
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');

.tin-root { position: relative; min-height: 100vh; width: 100%; background: #070708; font-family: 'Montserrat', sans-serif; }

.ambient { position: fixed; top: -300px; left: 50%; transform: translateX(-50%); width: 1000px; height: 800px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 10%, rgba(210,175,100,0.09) 0%, rgba(210,175,100,0.03) 30%, transparent 65%); }
.ambient-floor { position: fixed; bottom: -200px; left: 50%; transform: translateX(-50%); width: 700px; height: 400px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 100%, rgba(210,175,100,0.03) 0%, transparent 60%); }
.grain { position: fixed; inset: 0; z-index: 1; pointer-events: none; opacity: 0.5; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.12'/%3E%3C/svg%3E"); background-size: 180px 180px; }
.frame { position: fixed; inset: 20px; border: 1px solid rgba(210,175,100,0.07); z-index: 2; pointer-events: none; }
.frame::before, .frame::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }
.frame::after  { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner { position: fixed; inset: 20px; z-index: 2; pointer-events: none; }
.frame-inner::before, .frame-inner::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame-inner::before { top: -1px; right: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner::after  { bottom: -1px; left: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }

.page { position: relative; z-index: 10; max-width: 760px; margin: 0 auto; padding: 56px 24px 100px; animation: fadeUp 0.8s cubic-bezier(0.16,1,0.3,1) both; }
@keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

.wm-row { display: flex; align-items: center; gap: 14px; margin-bottom: 32px; }
.wm-line { flex: 1; height: 1px; background: linear-gradient(90deg, transparent, rgba(210,175,100,0.2)); }
.wm-line.r { background: linear-gradient(90deg, rgba(210,175,100,0.2), transparent); }
.wm-text { font-size: 11px; font-weight: 600; letter-spacing: 4px; color: rgba(210,175,100,0.5); text-transform: uppercase; white-space: nowrap; }

.back-link { display: inline-flex; align-items: center; gap: 8px; background: none; border: none; color: rgba(237,232,223,0.4); font-family: 'Montserrat', sans-serif; font-size: 12px; font-weight: 500; letter-spacing: 0.5px; cursor: pointer; padding: 0; margin-bottom: 32px; transition: color 0.3s; }
.back-link:hover { color: rgba(210,175,100,0.8); }
.back-arrow { font-size: 14px; }

.section-hd { display: flex; align-items: center; gap: 20px; margin-bottom: 20px; }
.section-rule { flex: 1; height: 1px; background: linear-gradient(90deg, rgba(210,175,100,0.18), transparent); }
.section-rule.l { background: linear-gradient(90deg, transparent, rgba(210,175,100,0.18)); flex: 0 0 24px; }
.section-eyebrow { font-size: 9px; font-weight: 600; letter-spacing: 4px; color: rgba(210,175,100,0.45); text-transform: uppercase; white-space: nowrap; }
.page-title { font-family: 'Cormorant', serif; font-size: 44px; font-weight: 300; color: #edeae2; letter-spacing: -0.5px; margin-bottom: 10px; line-height: 1.1; }
.page-title em { font-style: italic; color: rgba(210,175,100,0.8); font-weight: 300; }
.page-sub { font-size: 12px; font-weight: 500; letter-spacing: 1px; color: rgba(237,232,223,0.25); text-transform: uppercase; margin-bottom: 40px; }

/* ── TABLE OF CONTENTS ── */
.toc { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; margin-bottom: 48px; }
.toc-item { display: flex; align-items: center; gap: 10px; background: rgba(210,175,100,0.03); border: 1px solid rgba(210,175,100,0.1); padding: 10px 14px; cursor: pointer; text-align: left; transition: border-color 0.3s, background 0.3s; }
.toc-item:hover { border-color: rgba(210,175,100,0.3); background: rgba(210,175,100,0.06); }
.toc-num { font-family: 'Cormorant', serif; font-size: 14px; color: rgba(210,175,100,0.6); flex-shrink: 0; }
.toc-title { font-size: 11px; font-weight: 500; letter-spacing: 0.3px; color: rgba(237,232,223,0.6); }

/* ── DOCUMENT ── */
.doc { display: flex; flex-direction: column; gap: 40px; margin-bottom: 48px; }
.doc-section { border-top: 1px solid rgba(210,175,100,0.08); padding-top: 28px; scroll-margin-top: 32px; }
.doc-section:first-child { border-top: none; padding-top: 0; }
.doc-h2 { font-family: 'Cormorant', serif; font-size: 24px; font-weight: 400; color: #edeae2; margin-bottom: 4px; letter-spacing: -0.2px; }
.doc-sub { font-size: 10px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(210,175,100,0.4); margin-bottom: 16px; }
.doc-p { font-size: 14px; font-weight: 300; line-height: 1.85; color: rgba(237,232,223,0.55); margin-bottom: 12px; }
.doc-p:last-child { margin-bottom: 0; }
.doc-p >>> strong { color: rgba(237,232,223,0.8); font-weight: 500; }
.doc-p >>> a { color: rgba(210,175,100,0.8); }
.doc-list { display: flex; flex-direction: column; gap: 12px; padding-left: 20px; margin: 16px 0; }
.doc-list li { font-size: 14px; font-weight: 300; line-height: 1.75; color: rgba(237,232,223,0.5); list-style: none; position: relative; }
.doc-list li::before { content: ''; position: absolute; left: -20px; top: 9px; width: 5px; height: 5px; border-radius: 50%; background: rgba(210,175,100,0.5); }
.doc-list li >>> strong { color: rgba(237,232,223,0.8); font-weight: 500; }

.doc-alert { border-left: 2px solid rgba(210,110,90,0.6); background: rgba(210,110,90,0.06); padding: 14px 18px; font-size: 13px; font-weight: 300; line-height: 1.7; color: rgba(237,200,195,0.85); margin: 16px 0; }
.doc-alert >>> strong { font-weight: 600; }

/* ── ACTIONS ── */
.actions-row { margin-bottom: 24px; }
.btn-close { width: 100%; display: flex; align-items: center; justify-content: center; gap: 14px; border: 1px solid rgba(210,175,100,0.3); background: transparent; padding: 16px; color: rgba(210,175,100,0.8); font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 600; letter-spacing: 3px; text-transform: uppercase; cursor: pointer; text-decoration: none; transition: border-color 0.3s, background 0.3s; box-sizing: border-box; }
.btn-close:hover { border-color: rgba(210,175,100,0.6); background: rgba(210,175,100,0.06); }
.btn-tick { width: 18px; height: 1px; background: rgba(210,175,100,0.4); flex-shrink: 0; }

/* ── FOOTER ── */
.footer-links { display: flex; align-items: center; justify-content: center; gap: 14px; margin-bottom: 8px; }
.footer-link { color: rgba(210,175,100,0.6); font-size: 12px; text-decoration: none; }
.footer-link:hover { color: rgba(210,175,100,0.9); }
.footer-sep { color: rgba(237,232,223,0.2); }
.footer-copyright { text-align: center; font-size: 11px; color: rgba(237,232,223,0.25); }

@media (max-width: 600px) {
  .page { padding: 40px 16px 80px; }
  .frame, .frame-inner { inset: 12px; }
  .page-title { font-size: 32px; }
  .toc { grid-template-columns: 1fr; }
}
@media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; } }
</style>